import { useState } from "react";
import { toast } from "sonner";
import { Lock, RefreshCw, Mail, Send, CalendarDays, FileText, Paperclip, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { PageHeader } from "@/components/site/PageHeader";
import { OnboardingPanel, AGENCY_STEPS, welcomeMessage } from "@/pages/AdminApplicantsPage";

const API = process.env.REACT_APP_BACKEND_URL;

const lastSeen = (key) => Number(localStorage.getItem(`admin_seen_${key}`) || 0);
const markSeen = (key) => localStorage.setItem(`admin_seen_${key}`, String(Date.now()));
const countNew = (items, since) => items.filter((i) => new Date(i.created_at).getTime() > since).length;

const inputCls =
  "w-full rounded-xl border border-white/15 bg-black/40 px-4 py-3 font-body text-sm text-white placeholder:text-white/30 focus:border-[#d4af37] focus:outline-none";

const TEMPLATES = [
  { value: "reply", label: "General Reply" },
  { value: "life_quote", label: "Life Insurance Quote" },
  { value: "legal_quote", label: "LegalShield Quote" },
];

const Composer = ({ open, onClose, code, initial }) => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const send = async (e) => {
    e.preventDefault();
    if (!form.to_email || !form.to_name || !form.subject || !form.message) {
      toast.error("All fields are required.");
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/send-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Admin-Code": code },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success(`Email sent to ${form.to_email}`);
      onClose();
    } catch {
      toast.error("Failed to send. Try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="admin-composer" className="max-w-xl max-h-[85vh] overflow-y-auto border-[#bf953f]/30 bg-[#0a192f] p-8">
        <DialogTitle className="font-serif-display text-2xl font-medium text-white flex items-center gap-3">
          <Mail size={20} className="text-[#d4af37]" /> Send Branded Email
        </DialogTitle>
        <form onSubmit={send} className="mt-4 space-y-4">
          <select data-testid="composer-template" className={inputCls} value={form.template} onChange={set("template")}>
            {TEMPLATES.map((t) => (
              <option key={t.value} value={t.value} className="bg-[#0a192f]">{t.label}</option>
            ))}
          </select>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input data-testid="composer-name" className={inputCls} placeholder="Recipient name *" value={form.to_name} onChange={set("to_name")} />
            <input data-testid="composer-email" type="email" className={inputCls} placeholder="Recipient email *" value={form.to_email} onChange={set("to_email")} />
          </div>
          <input data-testid="composer-subject" className={inputCls} placeholder="Subject *" value={form.subject} onChange={set("subject")} />
          <textarea data-testid="composer-message" rows={7} className={inputCls} placeholder="Your message (quote details, reply, next steps...) *" value={form.message} onChange={set("message")} />
          <p className="font-body text-[11px] text-white/35 leading-relaxed">
            Sent with the Heritage logo header, your branding, and a call-to-action button matched to the template.
          </p>
          <button
            type="submit"
            disabled={sending}
            data-testid="composer-send-btn"
            className="btn-sweep w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-metallic px-8 py-3.5 font-body text-sm font-semibold tracking-wide text-black disabled:opacity-60"
          >
            {sending ? "Sending..." : "Send Email"} <Send size={15} />
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const emptyCompose = { to_email: "", to_name: "", subject: "", message: "", template: "life_quote" };

const NewPill = () => (
  <span className="rounded-full bg-[#d4af37] px-2.5 py-0.5 font-body text-[10px] font-bold uppercase tracking-widest text-black">New</span>
);

const TabButton = ({ active, onClick, testid, label, badge }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`relative rounded-full px-5 py-2.5 font-body text-sm tracking-wide transition-colors duration-300 ${
      active ? "bg-gold-metallic text-black font-semibold" : "border border-white/15 text-white/60 hover:text-white"
    }`}
  >
    {label}
    {badge > 0 && (
      <span data-testid={`${testid}-badge`} className="absolute -top-2 -right-2 inline-flex items-center justify-center min-w-[22px] h-[22px] rounded-full bg-red-500 px-1.5 font-body text-[11px] font-bold text-white shadow-lg">
        {badge}
      </span>
    )}
  </button>
);

export default function AdminQuotesPage() {
  const [code, setCode] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("quotes");
  const [quotes, setQuotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [seen, setSeen] = useState({ quotes: lastSeen("quotes"), messages: lastSeen("messages"), bookings: lastSeen("bookings"), applications: lastSeen("applications") });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [compose, setCompose] = useState(null);

  const removeApp = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    const res = await fetch(`${API}/api/careers/applications/${id}`, { method: "DELETE", headers: { "X-Admin-Code": code } });
    if (res.ok) { setApplications((a) => a.filter((x) => x.id !== id)); toast.success("Application deleted."); }
    else toast.error("Couldn't delete. Try again.");
  };

  const removeQuote = async (id) => {
    if (!window.confirm("Delete this quote request permanently?")) return;
    const res = await fetch(`${API}/api/quotes/${id}`, { method: "DELETE", headers: { "X-Admin-Code": code } });
    if (res.ok) { setQuotes((q) => q.filter((x) => x.id !== id)); toast.success("Quote deleted."); }
    else toast.error("Couldn't delete. Try again.");
  };

  const updateQuoteStatus = async (id, status) => {
    setQuotes((q) => q.map((x) => (x.id === id ? { ...x, status } : x)));
    const res = await fetch(`${API}/api/quotes/${id}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json", "X-Admin-Code": code },
      body: JSON.stringify({ status }),
    });
    if (res.ok) toast.success(`Quote marked ${status}`);
    else toast.error("Couldn't update status.");
  };

  const removeMessage = async (id) => {
    if (!window.confirm("Delete this message permanently?")) return;
    const res = await fetch(`${API}/api/contact/${id}`, { method: "DELETE", headers: { "X-Admin-Code": code } });
    if (res.ok) { setMessages((m) => m.filter((x) => x.id !== id)); toast.success("Message deleted."); }
    else toast.error("Couldn't delete. Try again.");
  };

  const removeBooking = async (id) => {
    if (!window.confirm("Delete this booking entry permanently?")) return;
    const res = await fetch(`${API}/api/bookings/${id}`, { method: "DELETE", headers: { "X-Admin-Code": code } });
    if (res.ok) { setBookings((b) => b.filter((x) => x.id !== id)); toast.success("Booking deleted."); }
    else toast.error("Couldn't delete. Try again.");
  };

  const saveAppOnboarding = async (id, onboarding) => {
    setApplications((a) => a.map((x) => (x.id === id ? { ...x, onboarding } : x)));
    const res = await fetch(`${API}/api/careers/applications/${id}/onboarding`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", "X-Admin-Code": code },
      body: JSON.stringify(onboarding),
    });
    if (res.ok) toast.success("Onboarding saved.");
    else toast.error("Couldn't save onboarding.");
  };

  const newCounts = {
    quotes: countNew(quotes, seen.quotes),
    messages: countNew(messages, seen.messages),
    bookings: countNew(bookings, seen.bookings),
    applications: countNew(applications, seen.applications),
  };

  const switchTab = (t) => {
    markSeen(tab);
    setSeen((s) => ({ ...s, [tab]: Date.now() }));
    setTab(t);
  };

  const load = async (e, accessCode = code) => {
    e && e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const headers = { "X-Admin-Code": accessCode };
      const [qRes, mRes, bRes, aRes] = await Promise.all([
        fetch(`${API}/api/quotes`, { headers }),
        fetch(`${API}/api/contact`, { headers }),
        fetch(`${API}/api/bookings`, { headers }),
        fetch(`${API}/api/careers/applications`, { headers }),
      ]);
      if ([qRes, mRes, bRes, aRes].some((r) => r.status === 401)) {
        setError("Invalid access code.");
        setAuthed(false);
        return;
      }
      if (![qRes, mRes, bRes, aRes].every((r) => r.ok)) throw new Error();
      setQuotes(await qRes.json());
      setMessages(await mRes.json());
      setBookings(await bRes.json());
      setApplications(await aRes.json());
      setAuthed(true);
    } catch {
      setError("Could not load data. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader overline="Private" title="Client" accent="inbox." subtitle="Quote requests, messages, and bookings — reply and send quotes without leaving the site." />

      <section className="relative pb-24 md:pb-32">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          {!authed ? (
            <form onSubmit={load} className="max-w-md rounded-3xl border border-[#bf953f]/25 bg-[#0a192f] p-8 space-y-4" data-testid="admin-login-form">
              <div className="flex items-center gap-2 text-[#d4af37]">
                <Lock size={18} />
                <span className="font-body text-sm tracking-widest uppercase">Access Code Required</span>
              </div>
              <input
                type="password"
                data-testid="admin-code-input"
                className={inputCls}
                placeholder="Enter access code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
              />
              {error && <p data-testid="admin-error" className="font-body text-sm text-red-400">{error}</p>}
              <button
                type="submit"
                disabled={loading || !code}
                data-testid="admin-login-btn"
                className="btn-sweep w-full rounded-full bg-gold-metallic px-8 py-3.5 font-body text-sm font-semibold tracking-wide text-black disabled:opacity-60"
              >
                {loading ? "Checking..." : "Unlock"}
              </button>
            </form>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
                <div className="flex flex-wrap gap-2">
                  <TabButton active={tab === "quotes"} onClick={() => switchTab("quotes")} testid="admin-tab-quotes" label={`Quotes (${quotes.length})`} badge={newCounts.quotes} />
                  <TabButton active={tab === "messages"} onClick={() => switchTab("messages")} testid="admin-tab-messages" label={`Messages (${messages.length})`} badge={newCounts.messages} />
                  <TabButton active={tab === "bookings"} onClick={() => switchTab("bookings")} testid="admin-tab-bookings" label={`Bookings (${bookings.length})`} badge={newCounts.bookings} />
                  <TabButton active={tab === "applications"} onClick={() => switchTab("applications")} testid="admin-tab-applications" label={`Applications (${applications.length})`} badge={newCounts.applications} />
                </div>
                <div className="flex items-center gap-4">
                  <button
                    onClick={() => setCompose({ ...emptyCompose })}
                    data-testid="admin-send-quote-btn"
                    className="btn-sweep inline-flex items-center gap-2 rounded-full bg-gold-metallic px-6 py-2.5 font-body text-sm font-semibold tracking-wide text-black"
                  >
                    <Mail size={15} /> Send a Quote
                  </button>
                  <button onClick={() => load()} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 font-body text-sm text-[#d4af37] hover:text-[#fcf6ba] transition-colors">
                    <RefreshCw size={15} /> Refresh
                  </button>
                </div>
              </div>

              {tab === "quotes" && (
                quotes.length === 0 ? <p className="font-body text-white/50">No quote requests yet.</p> : (
                  <div className="space-y-4">
                    {quotes.map((q) => (
                      <div key={q.id} data-testid={`admin-quote-${q.id}`} className={`rounded-2xl border bg-[#0a192f] p-6 ${new Date(q.created_at).getTime() > seen.quotes ? "border-[#d4af37]/50" : "border-white/10"}`}>
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <h3 className="font-serif-display text-2xl text-white flex items-center gap-3">
                            {q.name}
                            {new Date(q.created_at).getTime() > seen.quotes && <NewPill />}
                          </h3>
                          <div className="flex items-center gap-3">
                            <span className="font-body text-xs text-white/40">{new Date(q.created_at).toLocaleString()}</span>
                            <select
                              data-testid={`admin-quote-status-${q.id}`}
                              value={q.status || "New"}
                              onChange={(e) => updateQuoteStatus(q.id, e.target.value)}
                              className="rounded-full border border-white/20 bg-black/50 px-3 py-1.5 font-body text-xs font-bold uppercase tracking-wider text-[#d4af37] focus:outline-none"
                            >
                              {["New", "Contacted", "Quoted", "Closed"].map((s) => <option key={s} value={s} className="bg-[#0a192f] text-white">{s}</option>)}
                            </select>
                          </div>
                        </div>
                        <p className="mt-1 font-body text-sm text-gold-gradient inline-block">{q.coverage_type}</p>
                        <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 font-body text-sm text-white/70">
                          <a href={`tel:${q.phone}`} className="hover:text-[#d4af37]">{q.phone}</a>
                          <a href={`mailto:${q.email}`} className="hover:text-[#d4af37]">{q.email}</a>
                          <span>{q.state || "—"}</span>
                        </div>
                        {q.message && <p className="mt-3 font-body text-sm text-white/50 italic">"{q.message}"</p>}
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button
                            onClick={() => setCompose({ to_email: q.email, to_name: q.name, subject: `Your ${q.coverage_type} Quote — Heritage Family Protection Agency`, message: "", template: "life_quote" })}
                            data-testid={`admin-reply-quote-${q.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-[#bf953f]/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-[#d4af37] hover:border-[#d4af37] transition-colors"
                          >
                            <Mail size={13} /> Send Quote / Reply
                          </button>
                          <button
                            onClick={() => removeQuote(q.id)}
                            data-testid={`admin-delete-quote-${q.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === "messages" && (
                messages.length === 0 ? <p className="font-body text-white/50" data-testid="admin-no-messages">No messages yet.</p> : (
                  <div className="space-y-4">
                    {messages.map((m) => (
                      <div key={m.id} data-testid={`admin-message-${m.id}`} className={`rounded-2xl border bg-[#0a192f] p-6 ${new Date(m.created_at).getTime() > seen.messages ? "border-[#d4af37]/50" : "border-white/10"}`}>
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <h3 className="font-serif-display text-2xl text-white flex items-center gap-3">
                            {m.name}
                            {new Date(m.created_at).getTime() > seen.messages && <NewPill />}
                          </h3>
                          <span className="font-body text-xs text-white/40">{new Date(m.created_at).toLocaleString()}</span>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-4 font-body text-sm text-white/70">
                          <a href={`mailto:${m.email}`} className="hover:text-[#d4af37]">{m.email}</a>
                          {m.phone && <a href={`tel:${m.phone}`} className="hover:text-[#d4af37]">{m.phone}</a>}
                        </div>
                        <p className="mt-3 font-body text-sm text-white/60 leading-relaxed">{m.message}</p>
                        <div className="mt-4 flex flex-wrap gap-3">
                          <button
                            onClick={() => setCompose({ to_email: m.email, to_name: m.name, subject: "Re: Your message to Heritage Family Protection Agency", message: "", template: "reply" })}
                            data-testid={`admin-reply-message-${m.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-[#bf953f]/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-[#d4af37] hover:border-[#d4af37] transition-colors"
                          >
                            <Mail size={13} /> Reply
                          </button>
                          <button
                            onClick={() => removeMessage(m.id)}
                            data-testid={`admin-delete-message-${m.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === "bookings" && (
                bookings.length === 0 ? (
                  <p className="font-body text-white/50" data-testid="admin-no-bookings">No bookings captured yet. Bookings made through the website calendar will appear here.</p>
                ) : (
                  <div className="space-y-4">
                    {bookings.map((b) => (
                      <div key={b.id} data-testid={`admin-booking-${b.id}`} className={`rounded-2xl border bg-[#0a192f] p-6 flex flex-wrap items-center justify-between gap-4 ${new Date(b.created_at).getTime() > seen.bookings ? "border-[#d4af37]/50" : "border-white/10"}`}>
                        <div className="flex items-center gap-4">
                          <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-[#bf953f]/30 text-[#d4af37]">
                            <CalendarDays size={20} strokeWidth={1.5} />
                          </span>
                          <div>
                            <p className="font-serif-display text-xl text-white flex items-center gap-3">
                              Call booked via website
                              {new Date(b.created_at).getTime() > seen.bookings && <NewPill />}
                            </p>
                            <p className="font-body text-xs text-white/40">{new Date(b.created_at).toLocaleString()}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <a
                            href="https://calendly.com/app/scheduled_events/user/me"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-full border border-[#bf953f]/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-[#d4af37] hover:border-[#d4af37] transition-colors"
                          >
                            View in Calendly
                          </a>
                          <button
                            onClick={() => removeBooking(b.id)}
                            data-testid={`admin-delete-booking-${b.id}`}
                            className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-red-400 hover:bg-red-500/10 transition-colors"
                          >
                            <Trash2 size={13} /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {tab === "applications" && (
                applications.length === 0 ? (
                  <p className="font-body text-white/50" data-testid="admin-no-applications">No career applications yet. Applications submitted at /careers/apply will appear here.</p>
                ) : (
                  <div className="space-y-4">
                    {applications.map((a) => {
                      const flags = [
                        ["Owes a government agency", a.owes_government],
                        ["Judgements against them", a.has_judgements],
                        ["Felony (past 10 yrs)", a.felony_10yr],
                        ["Identity theft conviction", a.identity_theft],
                        ["License suspended/revoked", a.license_revoked],
                      ];
                      return (
                        <div key={a.id} data-testid={`admin-application-${a.id}`} className={`rounded-2xl border bg-[#0a192f] p-6 ${new Date(a.created_at).getTime() > seen.applications ? "border-[#d4af37]/50" : "border-white/10"}`}>
                          <div className="flex flex-wrap items-baseline justify-between gap-2">
                            <h3 className="font-serif-display text-2xl text-white flex items-center gap-3">
                              <FileText size={20} className="text-[#d4af37]" />
                              {a.name}
                              {new Date(a.created_at).getTime() > seen.applications && <NewPill />}
                            </h3>
                            <span className="font-body text-xs text-white/40">{new Date(a.created_at).toLocaleString()}</span>
                          </div>
                          <p className="mt-1 font-body text-sm text-gold-gradient inline-block">Financial Services Agent Application</p>
                          <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-2 font-body text-sm text-white/70">
                            <a href={`tel:${a.phone}`} className="hover:text-[#d4af37]">{a.phone}</a>
                            <a href={`mailto:${a.email}`} className="hover:text-[#d4af37]">{a.email}</a>
                            <span>DOB: {a.birthday || "—"}</span>
                          </div>
                          <p className="mt-2 font-body text-sm text-white/60">
                            {[a.address, a.city, a.state, a.zip_code].filter(Boolean).join(", ") || "No address provided"}
                          </p>
                          <div className="mt-4 flex flex-wrap gap-2">
                            {flags.map(([label, val]) => (
                              <span key={label} className={`rounded-full px-3 py-1 font-body text-[11px] tracking-wide ${val ? "bg-red-500/15 border border-red-500/40 text-red-300" : "border border-white/10 text-white/40"}`}>
                                {label}: {val ? "YES" : "No"}
                              </span>
                            ))}
                          </div>
                          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2 font-body text-sm text-white/60">
                            <span>1099 before: <span className="text-white/85">{a.held_1099 || "—"}</span></span>
                            <span>Licensed: <span className="text-white/85">{a.licensed || "—"}</span></span>
                            <span>Experience: <span className="text-white/85">{a.experience || "—"}</span></span>
                            <span>Business consulting: <span className="text-white/85">{a.business_consulting || "—"}</span></span>
                          </div>
                          {a.employment_history && (
                            <p className="mt-3 font-body text-sm text-white/50 whitespace-pre-line"><span className="text-[#d4af37]">Employment (last 5 yrs):</span> {a.employment_history}</p>
                          )}
                          <p className="mt-3 font-body text-xs text-white/40 italic">Signed: {a.signature || "—"} · {a.signature_date || "—"}</p>
                          <div className="mt-4 flex flex-wrap gap-3">
                            {a.resume_path && (
                              <a
                                href={`${API}/api/careers/resume/${a.resume_path}?code=${encodeURIComponent(code)}`}
                                data-testid={`admin-resume-${a.id}`}
                                className="inline-flex items-center gap-2 rounded-full bg-gold-metallic px-5 py-2 font-body text-xs tracking-widest uppercase text-black font-semibold"
                              >
                                <Paperclip size={13} /> Download Resume
                              </a>
                            )}
                            <button
                              onClick={() => setCompose({ to_email: a.email, to_name: a.name, subject: "Your application to Heritage Family Protection Agency", message: "", template: "reply" })}
                              data-testid={`admin-reply-application-${a.id}`}
                              className="inline-flex items-center gap-2 rounded-full border border-[#bf953f]/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-[#d4af37] hover:border-[#d4af37] transition-colors"
                            >
                              <Mail size={13} /> Reply
                            </button>
                            <button
                              onClick={() => removeApp(a.id)}
                              data-testid={`admin-delete-application-${a.id}`}
                              className="inline-flex items-center gap-2 rounded-full border border-red-500/40 px-5 py-2 font-body text-xs tracking-widest uppercase text-red-400 hover:bg-red-500/10 transition-colors"
                            >
                              <Trash2 size={13} /> Delete
                            </button>
                            <OnboardingPanel
                              item={a}
                              stepDefs={AGENCY_STEPS}
                              onSave={saveAppOnboarding}
                              accent="#d4af37"
                              onWelcome={() => setCompose({ to_email: a.email, to_name: a.name, subject: `Welcome to the team, ${(a.name || "").split(" ")[0]}! — Financial Services Agent`, message: welcomeMessage((a.name || "").split(" ")[0], "Financial Services Agent", a.onboarding), template: "reply" })}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )
              )}

              {compose && <Composer open onClose={() => setCompose(null)} code={code} initial={compose} />}
            </>
          )}
        </div>
      </section>
    </>
  );
}
